import React, { memo } from 'react';
import { getBezierPath, EdgeLabelRenderer } from '@xyflow/react';
import type { EdgeProps } from '@xyflow/react';
import type { TransitionEdgeData } from '../../hooks/usePlaybookEditor';

const TRIGGER_COLORS: Record<string, string> = {
  manual: 'bg-lex-accent-soft text-lex-accent',
  event: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  deadline: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  auto: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
};

const TransitionEdge: React.FC<EdgeProps> = memo(({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
  markerEnd,
}) => {
  const d = data as TransitionEdgeData;
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const strokeColor = selected ? '#3b82f6' : '#9ca3af';
  const strokeWidth = selected ? 2.5 : 1.5;
  const subItems = d.guardCount + d.validatorCount + d.actionCount;

  return (
    <>
      <path
        id={id}
        d={edgePath}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        markerEnd={markerEnd}
        className="transition-all duration-200"
      />
      <EdgeLabelRenderer>
        <div
          className="nodrag nopan pointer-events-auto"
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
          }}
        >
          <div
            className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-medium shadow-sm border transition-all ${
              selected
                ? 'border-lex-accent bg-white dark:bg-gray-800 ring-1 ring-lex-accent'
                : 'border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-600'
            }`}
          >
            <span className={`rounded-full px-1.5 py-px ${TRIGGER_COLORS[d.trigger_mode] ?? TRIGGER_COLORS.manual}`}>
              {d.trigger_mode}
            </span>
            <span className="text-gray-700 dark:text-gray-300 truncate max-w-[100px]">{d.label}</span>
            {subItems > 0 && (
              <span className="text-[8px] text-gray-400 dark:text-gray-500">
                ({subItems})
              </span>
            )}
            {d.is_collaborative && (
              <span className="text-[8px] text-amber-500" title="Collaborative (approval chain)">
                ✓
              </span>
            )}
          </div>
        </div>
      </EdgeLabelRenderer>
    </>
  );
});

TransitionEdge.displayName = 'TransitionEdge';

export default TransitionEdge;
